import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Heart, MessageCircle, Share2, ShoppingBag, UserPlus, UserCheck, Volume2, VolumeX, Play, Pause, Gift, Trophy, Languages, Layers, Download, Loader2, Repeat2, MessageSquare, Link2, Code2 } from "lucide-react";
import VerifiedBadge from "@/components/plyx/VerifiedBadge";
import { toast } from "sonner";
import GiftDrawer from "./GiftDrawer";
// iter-119mm: Re-enable the client-side branding pipeline. Production
// doesn't include ffmpeg (per Emergent Support), so server-side
// watermarking never completes on the live site. The client-side
// canvas + MediaRecorder pipeline runs entirely in the user's browser
// — burns the "plyx @handle" watermark directly on the video, then
// downloads the branded file. MP4 output is now prioritized in
// pickRecorderMime() (iter-119mm in plyxVideoDownload.js) so WhatsApp
// Status and Samsung Gallery accept it.
import { downloadPlyxVideo } from "@/lib/plyxVideoDownload";
import { getPlyxFeedMuted, setPlyxFeedMuted, subscribePlyxFeedMuted, playPlyxChime, clearPlyxVideoProgress } from "@/lib/plyxFeedPrefs";
import YouTubeEmbed from "@/components/plyx/YouTubeEmbed";
import { celebratePlyx } from "@/components/plyx/PlyxCelebration";

// Like-milestone helper — turns a raw threshold into the burst copy.
// We keep this outside the component so the gold-burst trigger is the
// same whether it fires from the optimistic like response or from the
// public WebSocket spectator channel.
const milestoneMessage = (n) => {
  if (!n) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M likes! 🏆`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K likes! 🚀`;
  return `${n} likes! 🎉`;
};

// Filter codes accepted by the backend; mirrored from PlyxUpload.jsx so the
// player styles match exactly what the creator picked.
const BEAUTIFY_CSS = {
  none:       "none",
  // Originals
  vivid:      "saturate(1.4) contrast(1.1)",
  warm:       "sepia(0.18) saturate(1.2) hue-rotate(-8deg)",
  cool:       "saturate(1.1) hue-rotate(8deg) brightness(1.02)",
  bw:         "grayscale(1) contrast(1.05)",
  vintage:    "sepia(0.5) saturate(0.85) contrast(0.95)",
  dreamy:     "blur(0.4px) saturate(1.15) brightness(1.05) contrast(0.95)",
  // Face-beauty v2
  smooth:     "blur(0.7px) saturate(1.1) brightness(1.06) contrast(0.96)",
  glow:       "brightness(1.12) saturate(1.18) contrast(1.05) hue-rotate(-4deg)",
  porcelain:  "brightness(1.1) saturate(0.9) contrast(1.02) hue-rotate(4deg)",
  kbeauty:    "brightness(1.08) saturate(1.05) contrast(0.94) sepia(0.06)",
  cinematic:  "contrast(1.18) saturate(0.92) brightness(0.97)",
  sunset:     "sepia(0.25) saturate(1.25) brightness(1.05) hue-rotate(-12deg)",
  golden:     "sepia(0.45) saturate(1.3) brightness(1.06)",
  fresh:      "brightness(1.1) saturate(1.3) contrast(1.04)",
  midnight:   "brightness(0.92) saturate(1.05) contrast(1.1) hue-rotate(14deg)",
  drama:      "contrast(1.3) saturate(0.7) brightness(0.96)",
};

const fmtCount = (n) => {
  if (!n) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

const currencySymbol = (c) => ({ USD: "$", GBP: "£", EUR: "€", CAD: "C$" }[c] || `${c} `);

// Show the "you've maxed out your watch-to-earn cap" toast at most once per
// session per cap period (so the user isn't nagged on every subsequent video
// once they hit the limit). Daily caps reset by date string, monthly by month.
const maybeShowCapToast = (capHit) => {
  try {
    const period = capHit === "monthly"
      ? new Date().toISOString().slice(0, 7)   // YYYY-MM
      : new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const key = `plyx_w2e_cap_toast_${capHit}_${period}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    if (capHit === "monthly") {
      toast(
        "You've hit your £40 monthly watch-to-earn cap — your balance is safe and resets next month.",
        { duration: 5000, icon: "🏆" }
      );
    } else if (capHit === "daily") {
      toast(
        "You've hit today's watch-to-earn cap — come back tomorrow to earn more.",
        { duration: 4000, icon: "⏳" }
      );
    }
  } catch (_e) { /* sessionStorage can fail in private mode — silently ignore */ }
};

// Predictive nudge: once a viewer crosses 90% of their monthly cap, show a
// single "you're close" toast so the cap-hit moment isn't a surprise. Fires
// at most once per calendar month per session.
const maybeShowNearCapToast = (earnedMonth, monthlyCap) => {
  if (typeof earnedMonth !== "number" || typeof monthlyCap !== "number" || monthlyCap <= 0) return;
  if (earnedMonth / monthlyCap < 0.9) return;
  try {
    const period = new Date().toISOString().slice(0, 7); // YYYY-MM
    const key = `plyx_w2e_near_cap_toast_${period}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    const left = Math.max(0, monthlyCap - earnedMonth);
    toast(
      `You're close — only £${left.toFixed(2)} left in this month's watch-to-earn cap.`,
      { duration: 4500, icon: "⚡" }
    );
  } catch (_e) { /* ignore */ }
};

/**
 * Inline "Captions" pill shown on videos that don't yet have captions.
 * Tapping it triggers caption FETCH on-demand (no auto-translation —
 * captions come back in the source language):
 *   • Native uploads → POST /plyx/videos/:id/captions/translate
 *     with `lang: "original"` (Whisper runs server-side if needed)
 *   • YouTube embeds → POST /plyx/videos/:id/captions/translate
 *     with `lang: "original"` (yt-dlp under the hood)
 *
 * Translation only happens when the viewer ALSO picks a target
 * language from the language picker that appears once captions land.
 * This is the explicit "let the user decide, don't auto-translate"
 * behaviour the feed needs — previously this button posted
 * `lang: "en"` and the backend silently translated every non-English
 * clip on first tap.
 *
 * Polls /captions every 4s while generating so the chip flips into the
 * full CC + language picker once the result lands — no manual reload.
 */
const CaptionRequestButton = ({ video, onSegmentsReady }) => {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(video.captions_status || null);
  const isEmbed = video.media_type === "embed";

  // Auto-retry the fetch call after a brief delay when the first
  // attempt returns the "captions are being generated" error. The
  // backend fetches+caches captions on the first call, but Cloudflare
  // can cut the long request short; the *next* call usually hits the
  // freshly cached row and returns instantly. Combined with the polling
  // GET below, this means the user never has to manually retry.
  useEffect(() => {
    if (status !== "generating" && status !== "queued") return;
    let alive = true;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        // First try the fetch-only path — surfaces captions in the
        // SOURCE language without auto-translating. The translation
        // step only happens when the viewer picks a language from
        // the picker below.
        const r = await plyxApi.post(
          `/plyx/videos/${video.video_id}/captions/translate`,
          { lang: "original" },
        );
        if (!alive) return;
        if (r.data?.segments?.length) {
          onSegmentsReady(r.data.segments, r.data?.source_language || "auto");
          setStatus("ready_yt");
          toast.success("Captions ready — pick a language to translate", { duration: 2500 });
          return;
        }
      } catch (e) {
        // Still generating? keep polling. Other errors → fall through
        // to the GET /captions probe below as a safety net.
      }
      try {
        const r = await plyxApi.get(`/plyx/videos/${video.video_id}/captions`);
        if (!alive) return;
        const st = r.data?.status;
        if (st === "ready" || st === "ready_yt") {
          setStatus(st);
          onSegmentsReady(r.data.segments || [], r.data.language);
          toast.success("Captions ready — pick a language to translate", { duration: 2500 });
          return;
        }
      } catch { /* ignore */ }
      if (attempts < 20 && alive) setTimeout(tick, 4000);
      else if (alive) {
        setStatus("failed");
        toast.error("This video has no captions available.");
      }
    };
    tick();
    return () => { alive = false; };
  }, [status, video.video_id, onSegmentsReady]);

  const start = async () => {
    if (busy) return;
    setBusy(true);
    // Flip immediately into the persistent loading state so the user
    // sees clear progress even if the request takes 25-30s. The toast
    // is a one-line hint; the BUTTON itself stays in spinner form for
    // the whole duration.
    setStatus("generating");
    toast.message(
      isEmbed
        ? "Fetching captions… (~15s)"
        : "Generating captions… this can take 20–60s.",
      { duration: 3000 },
    );
    try {
      // `lang: "original"` → backend ensures captions exist (yt-dlp
      // or Whisper) and returns them in the SOURCE language with
      // NO translation. The viewer must explicitly pick a target
      // language afterwards to translate — keeping the feed
      // experience entirely opt-in.
      const r = await plyxApi.post(
        `/plyx/videos/${video.video_id}/captions/translate`,
        { lang: "original" },
      );
      if (r.data?.segments?.length) {
        onSegmentsReady(r.data.segments, r.data?.source_language || "auto");
        setStatus("ready_yt");
        toast.success("Captions ready — pick a language to translate");
      } else {
        toast.error("No spoken audio detected on this clip.");
        setStatus("skipped_no_audio");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      // Backend still working on the captions — the polling effect
      // above will pick them up automatically, so we keep the spinner
      // visible and DON'T show a confusing "retry" toast.
      if (typeof detail === "string" && detail.toLowerCase().includes("no captions")) {
        // status is already "generating" → polling effect runs.
      } else {
        setStatus(null);
        toast.error(detail || "Could not fetch captions for this clip.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (status === "generating" || status === "queued") {
    return (
      <div
        className="px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white inline-flex items-center gap-1.5 shadow-md"
        data-testid={`plyx-cc-pending-${video.video_id}`}
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading captions…
      </div>
    );
  }
  if (status === "skipped_no_audio" || status === "failed" || status === "skipped_corrupted") {
    return (
      <div
        className="px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider bg-black/60 backdrop-blur text-white/60 border border-white/15"
        data-testid={`plyx-cc-silent-${video.video_id}`}
      >
        {status === "skipped_no_audio" ? "Silent" : "No captions"}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={start}
      disabled={busy}
      data-testid={`plyx-cc-request-${video.video_id}`}
      title="Show captions for this video (pick a language afterwards to translate)"
      className="px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider bg-black/60 backdrop-blur text-white border border-white/25 hover:bg-black/80 inline-flex items-center gap-1.5 disabled:opacity-60"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
      Captions
    </button>
  );
};

/**
 * Vertical full-screen video card for Plyx feed.
 * Auto-plays when in view, pauses when out. Tap to mute/pause, swipe nav done by parent.
 */
// iter-125: detect constrained networks ONCE. On 2g/3g/save-data we boot
// ONLY the active card's YouTube player — booting neighbours steals the
// active clip's bandwidth and is the #1 cause of "every video hangs" on
// mobile. On fast connections we still pre-boot the NEXT card (swipe-
// forward is the dominant gesture); the PREV card is never pre-booted —
// its poster shows instantly and the player boots in ~1s on swipe-back.
const PLYX_SLOW_NET = (() => {
  try {
    const c = navigator.connection || navigator.webkitConnection;
    return !!c && (c.saveData || /(slow-2g|2g|3g)/.test(c.effectiveType || ""));
  } catch { return false; }
})();
// iter-125: consecutive auto-skip guard. If the network itself is dead,
// EVERY card fails to boot — without a cap the feed would eat itself,
// removing card after card. After 3 consecutive boot failures we stop
// auto-skipping (the "Watch on YouTube" fallback stays available); any
// successfully painted frame resets the streak.
let _plyxBootFailStreak = 0;

const VideoCard = ({ video, isActive, isNext, isPrev, onLikeToggle, onCommentClick, onFollowToggle, onEmbedRemove }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  // iter-121: true while the active native video is stalled/buffering —
  // drives the centre spinner so slow loads don't look like a frozen app.
  const [buffering, setBuffering] = useState(false);
  // iter-121: remembers a DELIBERATE viewer pause (tap) so the stall
  // watchdog never force-resumes a video the user chose to stop.
  const userPausedRef = useRef(false);
  // Mute preference is session-wide — once the user unmutes one video
  // in the feed, every subsequent swipe also plays unmuted (and we
  // persist that via localStorage so it survives reloads).
  const [muted, setMuted] = useState(getPlyxFeedMuted);
  // iter-119h: track the ACTUAL <video>.muted DOM state separately from
  // the user-preference state above. They diverge whenever Chrome's
  // autoplay policy refuses unmuted autoplay → our fallback forcibly
  // sets `v.muted = true` even though `getPlyxFeedMuted() === false`.
  // Without this, the "Tap for sound" pill (gated on `muted`) never
  // renders, leaving viewers with a silent video and no way to unmute.
  const [domMuted, setDomMuted] = useState(true);
  // iter-119i: track whether the viewer has EXPLICITLY unmuted at any
  // point during this card's lifetime — via PiP controls, native video
  // controls, the "Tap for sound" pill, the top-right toggle, or any
  // play-tap. Once true, NO programmatic re-mute is allowed (autoplay
  // fallback, callback ref, or subscription handler). Fixes the
  // "click PiP unmute → briefly unmutes then re-mutes" symptom that
  // Tobechukwu reported (screenshot of Chrome PiP mini-player).
  const hasBeenUnmutedRef = useRef(false);
  useEffect(() => subscribePlyxFeedMuted((m) => {
    setMuted(m);
    // Also flip the live <video> element imperatively. The React-bound
    // `muted` attribute can't override Android Chrome's autoplay policy
    // (which silently re-mutes the element if autoplay-with-sound is
    // blocked), so we set `.muted` on the DOM node directly — which
    // works because the toggle click *is* a real user gesture.
    try {
      if (videoRef.current) videoRef.current.muted = m;
    } catch { /* ignore */ }
    // YouTube embed: drive the live YT.Player instance directly. The
    // toggle click is a real user gesture, so Safari/Chrome allow the
    // unmute-with-sound action immediately.
    try {
      const p = ytPlayerRef.current;
      if (p) {
        if (m) {
          p.mute && p.mute();
        } else {
          p.unMute && p.unMute();
          p.setVolume && p.setVolume(100);
        }
      }
    } catch { /* ignore */ }
  }), []);
  const [liked, setLiked] = useState(!!video.liked_by_me);
  const [likeCount, setLikeCount] = useState(video.like_count || 0);
  // iter-119m: repost state. Optimistic toggle + animated burst on tap
  // so users get instant feedback even on slow connections.
  const [reposted, setReposted] = useState(!!video.reposted_by_me);
  const [repostCount, setRepostCount] = useState(video.repost_count || 0);
  const [repostBusy, setRepostBusy] = useState(false);
  const [followed, setFollowed] = useState(!!video.followed_by_me);
  const [progress, setProgress] = useState(0);   // 0..1, drives the bottom scrubber
  const lastProgressRef = useRef(0);             // throttle gate for scrubber re-renders
  const [heartBurst, setHeartBurst] = useState(0); // re-keys the burst element for replay
  const [giftDrawerOpen, setGiftDrawerOpen] = useState(false);
  const [floatingGifts, setFloatingGifts] = useState([]); // [{id, emoji, coins}]
  const [giftStats, setGiftStats] = useState({ total: 0 });
  // Captions are OFF by default (iter-114, per Emergent Support
  // recommendation). Source-language subtitles auto-appearing on
  // multi-language clips (Swahili, Yoruba, etc.) looked like
  // automatic translation to viewers. They now only appear when
  // the viewer deliberately taps the CC button.
  const [captionsOn, setCaptionsOn] = useState(false);
  const [shopSheetOpen, setShopSheetOpen] = useState(false);
  const [captionLine, setCaptionLine] = useState("");
  const [captionLang, setCaptionLang] = useState("auto");      // "auto" = original, else lang code
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [translatedSegs, setTranslatedSegs] = useState(null);  // overrides captionSegs when set
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  // iter-119w: "Save for WhatsApp Status" 30s cut state.
  const [waSaving, setWaSaving] = useState(false);
  const tapTimerRef = useRef(null);
  // YouTube IFrame Player handle (set via YouTubeEmbed's onReady). Used
  // to drive mute/unmute on the active card so the global mute toggle
  // in the action sidebar also affects embedded videos.
  const ytPlayerRef = useRef(null);
  // True once the YouTube IFrame API reports an embed-disabled error
  // (codes 101/150) for this card. Drives the "Watch on YouTube" fall-
  // back overlay so creators don't see a permanently black tile when
  // the uploader (Vevo, music labels) has restricted embedding.
  const [embedBlocked, setEmbedBlocked] = useState(false);
  useEffect(() => { setEmbedBlocked(false); }, [video?.embed_video_id]);

  // For YouTube embed cards, fire the Plyx chime when the card becomes
  // active. The user-upload <video> path already triggers it on metadata
  // load — embeds bypass that handler entirely, so without this they'd
  // never get the brand audio signature.
  useEffect(() => {
    if (video.media_type !== "embed" || video.embed_provider !== "youtube") return;
    if (!isActive) return;
    playPlyxChime(video.video_id);
  }, [isActive, video.media_type, video.embed_provider, video.video_id]);
  const { user: me } = usePlyxAuth();
  const navigate = useNavigate();
  const viewedRef = useRef(false);

  // Sync local state if parent refreshes video object
  useEffect(() => {
    setLiked(!!video.liked_by_me);
    setLikeCount(video.like_count || 0);
    setFollowed(!!video.followed_by_me);
  }, [video.video_id, video.liked_by_me, video.like_count, video.followed_by_me]);

  // Pull recent gift stats once when the card becomes active — deferred
  // ~800ms so the fetch doesn't compete with the swipe settle animation.
  useEffect(() => {
    if (!isActive) return;
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await plyxApi.get(`/plyx/videos/${video.video_id}/gifts?limit=8`);
        if (!alive) return;
        setGiftStats({
          total: r.data.total_gifts || 0,
          topGifters: r.data.top_gifters || [],
        });
      } catch (_e) { /* silent */ }
    }, 800);
    return () => { alive = false; clearTimeout(t); };
  }, [isActive, video.video_id]);

  // Whisper auto-captions — fetched lazily once per active video.
  const [captionSegs, setCaptionSegs] = useState(null);
  useEffect(() => {
    if (!isActive) { setCaptionSegs(null); setCaptionLine(""); setTranslatedSegs(null); setCaptionLang("auto"); return; }
    if (video.captions_status !== "ready") return;
    let alive = true;
    (async () => {
      try {
        const r = await plyxApi.get(`/plyx/videos/${video.video_id}/captions`);
        if (!alive) return;
        setCaptionSegs(r.data?.segments || []);
      } catch (_e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, [isActive, video.video_id, video.captions_status]);

  // ── (Removed iter-107 / cleaned iter-110) ────────────────────────
  // The viewer used to be auto-translated on every active video if
  // they'd EVER picked a language on any clip, plus an auto-popping
  // "Watch in {Lang}?" chip would surface when the video's spoken
  // language differed from the browser locale. Multiple users
  // complained the translator was kicking in unsolicited and
  // covering the screen — they want it deliberate and manual only.
  //
  // Translation is now opt-in: tap the language picker pill (always
  // visible bottom-left) → pick a target language. No browser-locale
  // detection, no surprise translations on swipe. The viewerLang /
  // videoLang / SUPPORTED_TRANSLATE_LANGS scaffolding + the chip
  // helpers (autoChipDismissed, showTranslateChip,
  // acceptTranslateChip) were deleted in iter-110.

  const handleGiftSent = ({ emoji, coins }) => {
    // Spawn a floating emoji that rises and fades out — pure CSS keyframes.
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const lane = Math.random() * 60 + 20;   // horizontal lane, % from left
    const size = 28 + Math.min(60, Math.floor(coins / 100) * 4);  // bigger emoji for bigger gifts
    setFloatingGifts((arr) => [...arr, { id, emoji, lane, size }]);
    setGiftStats((s) => ({ ...s, total: (s.total || 0) + 1 }));
    setTimeout(() => setFloatingGifts((arr) => arr.filter((g) => g.id !== id)), 3200);
    // Refresh top-gifters so the ribbon reflects this send within ~1s.
    plyxApi.get(`/plyx/videos/${video.video_id}/gifts?limit=1`)
      .then((r) => setGiftStats((s) => ({ ...s, topGifters: r.data.top_gifters || s.topGifters })))
      .catch(() => { /* silent */ });
  };


  // Auto play/pause based on whether this card is the visible one.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Respect optional trim_start / trim_end metadata so the player loops
    // only the section the creator chose during upload.
    const tStart = (typeof video.trim_start === "number" && video.trim_start >= 0) ? video.trim_start : 0;
    const tEnd = (typeof video.trim_end === "number" && video.trim_end > tStart) ? video.trim_end : null;
    if (isActive) {
      // Always restart from the creator's chosen trim_start when a video
      // becomes active in the swipe stack. The previous resume-from-
      // last-position behaviour confused users coming back to the feed
      // and dropped them mid-clip; per product call we now match
      // TikTok/Reels — every swipe is a fresh play from the top.
      // We also wipe the persisted progress so future visits don't try
      // to resume from a stale watermark either.
      try { clearPlyxVideoProgress(video.video_id); } catch { /* ignore */ }
      try { v.currentTime = tStart; } catch { /* ignore */ }
      // Plyx audio signature — fires once per (video × session). Skipped
      // automatically when the feed is muted so we don't dub over a
      // silent UX.
      playPlyxChime(video.video_id);
      // iter-119e: REGRESSION FIX (Tobechukwu @tobest, plyx.co.uk).
      //
      // Iter-116 introduced `v.muted = true` BEFORE play() and deferred
      // the unmute until AFTER play() resolved. On iOS Safari + Android
      // Chrome a deferred `v.muted = false` is SILENTLY BLOCKED on a
      // fresh page load when no user gesture has happened on THIS page
      // yet — which is exactly the gallery-tile → /plyx/v/:id flow. So
      // every gallery click ended up at a permanently-muted video.
      //
      // The right pattern (matches TikTok/Reels): TRY the desired mute
      // state on the FIRST play() call — the browser honours it if the
      // navigation came from a user gesture (a tap on the gallery
      // thumbnail counts). Only fall back to muted-play if that gets
      // rejected. After successful muted-play, the centre "Tap for
      // sound" pill (iter-119) plus the play-tap-also-unmutes path
      // (iter-119b/c) give the viewer one easy way to recover.
      const wantUnmuted = !getPlyxFeedMuted() || hasBeenUnmutedRef.current;
      try { v.muted = !wantUnmuted; } catch { /* ignore */ }
      // iter-120: fast-swipe cancellation guard. When the viewer flicks
      // through cards quickly this effect's cleanup runs while play()'s
      // promise is still pending — the subsequent pause() rejects it
      // with AbortError and the old retry path re-played a card that
      // was no longer on screen (audio ghosting + the landed card left
      // paused → the "frozen, needs a double-tap" symptom). Every async
      // callback below now bails once `cancelled` flips.
      let cancelled = false;
      const tryPlay = () => {
        try {
          const p = v.play();
          if (p && typeof p.then === "function") {
            p.then(() => {
              if (cancelled) return;
              setPlaying(true);
              // iter-119e belt-and-braces: even when play() resolves,
              // Android Chrome can silently re-mute between the
              // `v.muted = !wantUnmuted` line above and the actual
              // playback frame — re-assert the desired state once we
              // know play succeeded. This is a no-op on Safari but
              // recovers the rare Android Chrome edge case.
              try {
                if (videoRef.current && wantUnmuted) videoRef.current.muted = false;
              } catch { /* ignore */ }
            }).catch(() => {
              // Browser rejected play() — most likely because we
              // asked for sound on a fresh tab without a user gesture.
              // iter-119i: if the user has ALREADY explicitly unmuted
              // at any point, DO NOT force-mute again. Just retry the
              // play() with the user's explicit preference honoured —
              // if it still fails, the centre "Tap for sound" pill is
              // visible thanks to `domMuted` so they can act.
              // iter-120: an AbortError from a fast swipe-away lands
              // here too — `cancelled` stops the retry from re-playing
              // an off-screen card.
              if (cancelled) return;
              setTimeout(() => {
                try {
                  if (cancelled || !videoRef.current) return;
                  if (!hasBeenUnmutedRef.current) {
                    videoRef.current.muted = true;
                  }
                  videoRef.current.playsInline = true;
                  const retry = videoRef.current.play();
                  if (retry && retry.then) retry.then(() => { if (!cancelled) setPlaying(true); }).catch(() => { if (!cancelled) setPlaying(false); });
                } catch { if (!cancelled) setPlaying(false); }
              }, 60);
            });
          } else {
            // Older browsers — play() returned void; assume success.
            setPlaying(true);
          }
        } catch {
          setPlaying(false);
        }
      };
      tryPlay();
      // iter-121/126/127: PROGRESS-AWARE STALL WATCHDOG. iter-126's rule
      // reloaded any video that hadn't reached readyState 3 by 7s — but
      // on slow mobile data a HEALTHY download can need longer, and
      // load() restarts it from byte 0, so slow connections entered a
      // reload-livelock and nothing ever played (the "is worse, all
      // hang" production report). We now listen to the element's own
      // 'progress' events (fired whenever bytes arrive) and ONLY force
      // a reload when the network has been silent for 6+ seconds while
      // the clip is unplayable — a true stall, never a slow load.
      userPausedRef.current = false;
      let lastNet = Date.now();
      const onNetProgress = () => { lastNet = Date.now(); };
      v.addEventListener("progress", onNetProgress);
      v.addEventListener("loadeddata", onNetProgress);
      const kick = (delay, allowReload) => setTimeout(() => {
        if (cancelled) return;
        const el = videoRef.current;
        if (!el) return;
        const unplayable = el.readyState < 3 && (el.currentTime || 0) < tStart + 0.3;
        const netIdle = Date.now() - lastNet > 6000;
        const starved = el.error || (allowReload && unplayable && netIdle && !userPausedRef.current);
        if (starved) {
          try { el.load(); } catch { /* ignore */ }
          try { el.currentTime = tStart; } catch { /* ignore */ }
          try { const rp = el.play(); rp && rp.catch && rp.catch(() => {}); } catch { /* ignore */ }
          lastNet = Date.now();
        } else if (el.paused && !userPausedRef.current) {
          try { const rp = el.play(); rp && rp.catch && rp.catch(() => {}); } catch { /* ignore */ }
        }
      }, delay);
      const w1 = kick(2500, false);
      const w2 = kick(7000, true);
      const w3 = kick(12000, true);
      const w4 = kick(18000, true);

      // iter-128: MID-PLAYBACK freeze recovery. The boot watchdog only
      // guards the start of a clip; playback can also stall halfway
      // (dropped range request, flaky cell handover). When the element
      // fires 'waiting'/'stalled' and 6s later the clock hasn't moved
      // with the network silent, nudge it (micro-reseek + play); still
      // frozen 6s after that → reload at the same position. The viewer
      // never has to swipe away and back again.
      let stallTimer = null;
      let stallNudged = false;
      const onStall = () => {
        if (stallTimer || !isActive) return;
        const stuckAt = videoRef.current ? (videoRef.current.currentTime || 0) : 0;
        const check = () => {
          stallTimer = null;
          if (cancelled) return;
          const el = videoRef.current;
          if (!el || userPausedRef.current) { stallNudged = false; return; }
          const moved = Math.abs((el.currentTime || 0) - stuckAt) > 0.25;
          if (moved || el.readyState >= 3) { stallNudged = false; return; }
          if (Date.now() - lastNet < 5500) { stallTimer = setTimeout(check, 6000); return; }
          if (!stallNudged) {
            stallNudged = true;
            try { el.currentTime = stuckAt + 0.001; } catch { /* ignore */ }
            try { const rp = el.play(); rp && rp.catch && rp.catch(() => {}); } catch { /* ignore */ }
            stallTimer = setTimeout(check, 6000);
          } else {
            stallNudged = false;
            try { el.load(); } catch { /* ignore */ }
            try { el.currentTime = stuckAt; } catch { /* ignore */ }
            try { const rp = el.play(); rp && rp.catch && rp.catch(() => {}); } catch { /* ignore */ }
          }
        };
        stallTimer = setTimeout(check, 6000);
      };
      v.addEventListener("waiting", onStall);
      v.addEventListener("stalled", onStall);
      viewedRef.current = false;  // reset for next view
      // Send the view ping when the user has watched ≥ 5s, with watched_sec
      // so the backend can award watch-to-earn credit.
      const sendView = () => {
        if (viewedRef.current) return;
        viewedRef.current = true;
        const watched = Math.max(0, v.currentTime || 0);
        plyxApi.post(`/plyx/videos/${video.video_id}/view`, { watched_sec: watched })
          .then((r) => {
            if (r?.data?.credit_earned && r?.data?.credit?.amount) {
              toast.success(`+£${r.data.credit.amount.toFixed(2)} earned`, { duration: 2000 });
              maybeShowNearCapToast(r.data.earned_month, r.data.monthly_cap);
            } else if (r?.data?.cap_hit) {
              maybeShowCapToast(r.data.cap_hit);
            }
          })
          .catch(() => { /* fire-and-forget */ });
      };
      // Either at 5s or when user pauses / scrolls off, whichever first.
      const onTime = () => {
        if ((v.currentTime || 0) >= 5) sendView();
        // Loop back to trim_start once we cross trim_end.
        if (tEnd != null && v.currentTime >= tEnd) {
          try { v.currentTime = tStart; } catch { /* ignore */ }
        }
        // Resume-position persistence is intentionally disabled now —
        // every swipe restarts the clip from `tStart` (TikTok/Reels
        // behaviour). Keeping the scrubber update below so the in-clip
        // progress bar still works while watching.
        // Update scrubber progress (0..1) — relative to the trimmed window
        // so the bar fills end-to-end inside the clip the creator wanted.
        const durNow = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : 0;
        const total = tEnd != null ? Math.max(0.01, tEnd - tStart) : durNow;
        const rel = Math.max(0, (v.currentTime || 0) - tStart);
        if (total > 0) {
          // Throttle scrubber updates to 2% steps — a full-card React
          // re-render on every timeupdate (4x/sec) measurably janks
          // iOS Safari scrolling.
          const p = Math.min(1, rel / total);
          if (Math.abs(p - lastProgressRef.current) >= 0.02 || p >= 1) {
            lastProgressRef.current = p;
            setProgress(p);
          }
        }
        // Caption line tracking lives in a dedicated effect below so
        // it doesn't trigger this play/pause effect to re-run.
      };
      // Once the viewer finishes the whole clip, drop the resume marker
      // so the next visit starts fresh.
      const onEnded = () => { clearPlyxVideoProgress(video.video_id); };
      v.addEventListener("timeupdate", onTime);
      v.addEventListener("ended", onEnded);
      return () => {
        cancelled = true;  // iter-120: kill pending play() retries on swipe-away
        v.removeEventListener("timeupdate", onTime);
        v.removeEventListener("ended", onEnded);
        v.removeEventListener("progress", onNetProgress);
        v.removeEventListener("loadeddata", onNetProgress);
        v.removeEventListener("waiting", onStall);
        v.removeEventListener("stalled", onStall);
        if (stallTimer) clearTimeout(stallTimer);
        sendView();  // also fire on unmount/scroll-off if at least started
      };
    } else {
      v.pause();
      setPlaying(false);
      lastProgressRef.current = 0;
      setProgress(0);
    }
  }, [isActive, video.video_id, video.trim_start, video.trim_end, video.duration_sec]);

  // Sync the active caption line independently from the play/pause
  // effect. Putting captionLine in the play-effect's deps used to fire
  // a restart-from-tStart every second (because captionLine flips on
  // every timeupdate frame), which both stalled play() and showed a
  // black frame on slower devices.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isActive) return;
    const onTime = () => {
      if (captionsOn && Array.isArray(captionSegs) && captionSegs.length > 0) {
        const segs = translatedSegs || captionSegs;
        const t = v.currentTime || 0;
        const seg = segs.find((s) => t >= s.start && t <= s.end + 0.25);
        setCaptionLine(seg ? seg.text : "");
      } else {
        setCaptionLine("");
      }
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [isActive, captionsOn, captionSegs, translatedSegs]);

  const togglePlay = () => {
    // iter-121: YouTube embed cards never had a working tap-to-play —
    // videoRef is null for them so this function silently no-op'd,
    // which is why a stuck embed didn't respond to "hitting play".
    // Drive the YT player directly instead.
    if (video.media_type === "embed") {
      const p = ytPlayerRef.current;
      if (!p) return;
      try {
        const st = p.getPlayerState ? p.getPlayerState() : -1;
        if (st === 1) p.pauseVideo();
        else p.playVideo();
      } catch { /* ignore */ }
      return;
    }
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      userPausedRef.current = false;
      // iter-119b: tapping the giant Play button is a clear user gesture
      // signalling intent to consume the media with sound. On TikTok/Reels
      // a tap-to-play also unmutes — match that. Without this, viewers
      // landing directly on /plyx/v/:id (e.g. from a profile gallery tile
      // tap) tap Play, get a muted video, and conclude the upload has no
      // audio. Flip the session-wide mute pref AND imperatively unmute
      // the element so the unmute survives the play() promise.
      try { v.muted = false; } catch { /* ignore */ }
      try { setPlyxFeedMuted(false); } catch { /* ignore */ }
      const p = v.play();
      if (p && typeof p.then === "function") {
        // Belt-and-braces: some Android Chrome builds re-mute between
        // the imperative flip above and the play() promise resolving.
        // Re-assert muted=false after the promise lands.
        p.then(() => { try { v.muted = false; } catch { /* ignore */ } }).catch(() => {});
      }
    } else {
      userPausedRef.current = true;
      v.pause();
    }
  };

  // Keep the React `playing` state in lock-step with the real <video>
  // element. The previous code only flipped `playing` after the autoplay
  // play() promise resolved — but if the browser stalled/buffered/auto-
  // paused (Android Chrome does this when the tab loses focus, iOS Safari
  // does this if the user lifts a finger mid-scroll), the state would
  // drift and the giant Play overlay would sit on top of a video that
  // was, in fact, playing. Listening to the native events keeps the
  // overlay accurate in every case.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => { setPlaying(true); setBuffering(false); };
    const onPause = () => setPlaying(false);
    // iter-121: buffering indicators — 'waiting'/'stalled' flip the
    // centre spinner on, 'canplay'/'playing' clear it.
    const onWaiting = () => setBuffering(true);
    const onCanPlay = () => setBuffering(false);
    // iter-119h: keep `domMuted` in lock-step with the real <video>
    // element so UI affordances (the centre "Tap for sound" pill in
    // particular) appear whenever the element is actually muted,
    // regardless of what the user-preference state says.
    const onVolumeChange = () => {
      const m = !!v.muted;
      setDomMuted(m);
      // iter-119i: any time the DOM reports an unmute, remember it for
      // the rest of this card's lifetime AND sync the session pref.
      // Picture-in-Picture and the native <video controls> mute toggle
      // both fire volumechange — this is how we capture those gestures
      // even though they don't go through any React handler.
      if (!m) {
        hasBeenUnmutedRef.current = true;
        try { setPlyxFeedMuted(false); } catch { /* ignore */ }
      }
    };
    setDomMuted(!!v.muted); // seed once on mount
    v.addEventListener("play", onPlay);
    v.addEventListener("playing", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("stalled", onWaiting);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("volumechange", onVolumeChange);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("playing", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("stalled", onWaiting);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("volumechange", onVolumeChange);
    };
  }, [video.video_id]);

  // Single-tap = play/pause, Double-tap = like + heart burst (TikTok pattern).
  // We delay the single-tap action by 250ms; if a second tap arrives, it cancels
  // the play-toggle and triggers the like instead.
  const handleVideoTap = () => {
    if (tapTimerRef.current) {
      // Double-tap detected
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
      setHeartBurst((n) => n + 1);
      if (!liked) {
        // Optimistic like (re-uses handleLike's logic via a force flag)
        if (!me) { navigate("/plyx/login"); return; }
        setLiked(true);
        setLikeCount((c) => c + 1);
        plyxApi.post(`/plyx/videos/${video.video_id}/like`)
          .then((r) => {
            onLikeToggle?.(video.video_id, true);
            // Server tells us when this like crossed a milestone (100,
            // 1k, 10k, …). Fire the gold burst the moment we know —
            // the same payload the WebSocket would broadcast, so the
            // liker doesn't have to wait for the WS round-trip.
            const ms = r?.data?.milestone;
            if (ms) celebratePlyx({
              message: milestoneMessage(ms),
              theme: "gold",
            });
          })
          .catch(() => {
            setLiked(false);
            setLikeCount((c) => c - 1);
            toast.error("Could not update like");
          });
      }
      return;
    }
    tapTimerRef.current = setTimeout(() => {
      tapTimerRef.current = null;
      // For RSS/news photo cards, a single-tap opens the source article in a
      // new tab — that's the most useful action for a headline tile. Native
      // photo uploads (no external_url) keep the play/pause toggle.
      if (video.media_type === "photo" && video.external_url) {
        try { window.open(video.external_url, "_blank", "noopener,noreferrer"); } catch { /* popup blocked */ }
        return;
      }
      // iter-119b: on a NATIVE video that is currently muted, treat the
      // single tap as "unmute" (TikTok / Reels pattern) instead of
      // pause/play. Otherwise viewers landing on /plyx/v/:id from a
      // profile gallery tap the video, expect sound, and instead get a
      // pause toggle — concluding their upload is silent. Embeds and
      // photos keep the original behaviour.
      const v = videoRef.current;
      if (
        v
        && video.media_type !== "embed"
        && video.media_type !== "photo"
        && (muted || v.muted)
        && video.has_audio !== false
      ) {
        try { v.muted = false; } catch { /* ignore */ }
        try { setPlyxFeedMuted(false); } catch { /* ignore */ }
        // Ensure playback continues (the unmute might race with a paused
        // state when the user lands fresh and autoplay hasn't fired yet).
        if (v.paused) { try { v.play().catch(() => {}); } catch { /* ignore */ } }
        return;
      }
      togglePlay();
    }, 250);
  };

  const handleLike = async () => {
    if (!me) { navigate("/plyx/login"); return; }
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => c + (next ? 1 : -1));
    try {
      if (next) {
        const r = await plyxApi.post(`/plyx/videos/${video.video_id}/like`);
        const ms = r?.data?.milestone;
        if (ms) celebratePlyx({
          message: milestoneMessage(ms),
          theme: "gold",
        });
      } else {
        await plyxApi.delete(`/plyx/videos/${video.video_id}/like`);
      }
      onLikeToggle?.(video.video_id, next);
    } catch {
      // revert
      setLiked(!next);
      setLikeCount((c) => c + (next ? -1 : 1));
      toast.error("Could not update like");
    }
  };

  // iter-119m: Repost — share the creator's video onto the viewer's
  // own profile gallery (separate "Reposts" tab on the profile page).
  // Idempotent on the server side; the optimistic toggle here reverts
  // on any 4xx/5xx so the UI never lies. Blocks reposting your own
  // clip with a friendly toast instead of a 400.
  const handleRepost = async () => {
    if (!me) { navigate("/plyx/login"); return; }
    if (me?.plyx_user_id === video.creator_id) {
      toast.info("You can't repost your own video.");
      return;
    }
    if (repostBusy) return;
    const next = !reposted;
    setRepostBusy(true);
    setReposted(next);
    setRepostCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      if (next) {
        await plyxApi.post(`/plyx/videos/${video.video_id}/repost`);
        toast.success("Reposted to your profile");
      } else {
        await plyxApi.delete(`/plyx/videos/${video.video_id}/repost`);
        toast.message("Repost removed");
      }
    } catch (e) {
      setReposted(!next);
      setRepostCount((c) => Math.max(0, c + (next ? -1 : 1)));
      toast.error(e?.response?.data?.detail || "Could not update repost");
    } finally {
      setRepostBusy(false);
    }
  };

  // ── Public WebSocket spectator channel ────────────────────────────
  // Subscribe only while this card is the active slide — opening a WS
  // for every video in the feed is wasteful and slow on mobile data.
  // The backend fans out `{type:"milestone", threshold: N}` whenever a
  // like crosses 100/1k/10k/…, so every viewer on the active card sees
  // the gold burst in real time.
  useEffect(() => {
    if (!isActive || !video?.video_id) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = backend.replace(/^http/, "ws");
    let ws;
    // Defer the WS handshake until after the swipe settles — opening a
    // socket at the exact moment of the snap animation was part of the
    // "hesitation between videos" on slower phones.
    const t = setTimeout(() => {
      try {
        ws = new WebSocket(`${wsBase}/api/ws/plyx/video/${video.video_id}`);
      } catch {
        return;
      }
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg?.type === "milestone" && msg.threshold) {
            celebratePlyx({
              message: milestoneMessage(msg.threshold),
              theme: "gold",
            });
            // Keep the visible counter in sync with the broadcast count
            // so two viewers don't see different numbers post-burst.
            if (typeof msg.like_count === "number") {
              setLikeCount(msg.like_count);
            }
          }
        } catch { /* malformed frame — ignore */ }
      };
    }, 900);
    return () => {
      clearTimeout(t);
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, [isActive, video?.video_id]);

  const handleFollow = async () => {
    if (!me) { navigate("/plyx/login"); return; }
    if (!video.creator?.handle) return;
    const next = !followed;
    setFollowed(next);
    try {
      if (next) await plyxApi.post(`/plyx/u/${video.creator.handle}/follow`);
      else await plyxApi.delete(`/plyx/u/${video.creator.handle}/follow`);
      onFollowToggle?.(video.creator.plyx_user_id, next);
    } catch {
      setFollowed(!next);
      toast.error("Could not update follow");
    }
  };

  const [shareOpen, setShareOpen] = useState(false);
  const shareUrl = `${window.location.origin}/api/share/v/${video.video_id}`;
  const embedCode = `<iframe src="${window.location.origin}/plyx/embed/${video.video_id}" width="360" height="640" style="border:0;border-radius:16px;overflow:hidden" allowfullscreen title="Plyx video"></iframe>`;

  // Share button opens a small sheet: native share, copy link, copy embed.
  const handleShare = () => setShareOpen(true);

  const doNativeShare = async () => {
    setShareOpen(false);
    plyxApi.post(`/plyx/videos/${video.video_id}/share`).catch(() => {});
    if (navigator.share) {
      try {
        await navigator.share({ title: video.caption?.slice(0, 60) || "Watch on Plyx", url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard");
      } catch {
        toast.message(shareUrl);
      }
    }
  };

  const doCopyLink = async () => {
    setShareOpen(false);
    plyxApi.post(`/plyx/videos/${video.video_id}/share`).catch(() => {});
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.message(shareUrl);
    }
  };

  const doCopyEmbed = async () => {
    setShareOpen(false);
    plyxApi.post(`/plyx/videos/${video.video_id}/share`).catch(() => {});
    try {
      await navigator.clipboard.writeText(embedCode);
      toast.success("Embed code copied — paste it into any website or blog");
    } catch {
      window.prompt("Copy this embed code:", embedCode);
    }
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadProgress(0);
    plyxApi.post(`/plyx/videos/${video.video_id}/share`).catch(() => {});
    try {
      // iter-119mm: CLIENT-SIDE watermarking. Production doesn't include
      // ffmpeg so server-side branding never completes on plyx.co.uk.
      // Instead we run the entire watermarking pipeline in the user's
      // browser using canvas + MediaRecorder — burns the "plyx @handle"
      // wordmark directly onto the video frames, then downloads the
      // resulting MP4. Modern iOS Safari + Chrome Android both support
      // native MP4 recording (pickRecorderMime prioritizes MP4 first).
      //
      // Takes ~video-duration + 2s to complete (real-time record). We
      // show a progress toast so the user knows work is happening.
      const rawUrl = video.video_url;
      if (!rawUrl) {
        toast.error("This video has no downloadable source.");
        return;
      }
      const brandingToastId = toast.loading("Adding Plyx watermark…", { duration: 120 * 1000 });
      try {
        const result = await downloadPlyxVideo(
          {
            videoUrl: plyxMediaUrl(rawUrl),
            handle: video.creator?.handle,
            videoId: video.video_id,
            mediaType: video.media_type,
          },
          (evt) => {
            const p = typeof evt === "number" ? evt : (evt?.progress || 0);
            setDownloadProgress(Math.min(0.95, Math.max(0.05, p)));
            const pct = Math.round(p * 100);
            const phase = evt?.phase || "encoding";
            const label = phase === "outro" ? "Adding Plyx outro"
              : phase === "loading" ? "Loading video"
              : "Adding Plyx watermark";
            toast.loading(`${label}… ${pct}%`, { id: brandingToastId, duration: 120 * 1000 });
          },
        );
        toast.dismiss(brandingToastId);
        setDownloadProgress(1);
        try { playPlyxChime(`download:${video.video_id}`); } catch { /* ignore */ }
        if (result?.watermarked) {
          toast.success("Saved with plyx @" + (video.creator?.handle || "creator") + " watermark · long-press in Gallery to post to WhatsApp");
        } else {
          // Browser didn't support MediaRecorder OR the pipeline failed
          // — the fallback inside downloadPlyxVideo already saved the raw
          // file, so the user still has their MP4.
          toast.success("Saved · watermark unavailable in this browser");
        }
        return;
      } catch (brandErr) {
        toast.dismiss(brandingToastId);
        console.warn("Client-side branding failed:", brandErr);
        // Ultimate fallback — direct raw download so the user is never
        // left with nothing.
        const filename = `plyx-${video.creator?.handle || "video"}-${video.video_id}.mp4`;
        const downloadUrl = plyxMediaUrl(rawUrl) + (rawUrl.includes("?") ? "&" : "?") + `download=1&filename=${encodeURIComponent(filename)}`;
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = filename;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        try { playPlyxChime(`download:${video.video_id}`); } catch { /* ignore */ }
        toast.success("Saved · long-press in Gallery to post to WhatsApp Status");
      }
    } catch (e) {
      console.warn("Plyx download failed:", e);
      toast.error("Could not download this clip. Try again or use Share.");
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  // iter-119w: Save for WhatsApp Status → server generates a 30s cut
  // with plyx @handle watermark + WA-safe encode. Client polls the
  // build endpoint and streams the result to disk. If the device
  // supports Web Share API level 2 with `files`, we open the share
  // sheet directly (one-tap post to WhatsApp Status).
  const handleWhatsAppSave = async () => {
    if (waSaving) return;
    if (video.media_type === "photo" || video.media_type === "embed") {
      toast.error("WhatsApp Status save is only available for uploaded videos.");
      return;
    }
    setWaSaving(true);
    try {
      // iter-119mm: Same client-side branding pipeline as handleDownload.
      // Runs in the browser (no ffmpeg needed on the server) so it works
      // on production. MediaRecorder produces MP4 on modern iOS/Android
      // browsers — WhatsApp Status accepts the file directly.
      if (!video.video_url) {
        toast.error("This video has no downloadable source.");
        return;
      }
      const brandingToastId = toast.loading("Preparing for WhatsApp Status…", { duration: 120 * 1000 });
      try {
        const result = await downloadPlyxVideo(
          {
            videoUrl: plyxMediaUrl(video.video_url),
            handle: video.creator?.handle,
            videoId: video.video_id,
            mediaType: video.media_type,
            whatsappStatus: true,  // iter-119nn: cap at ~30s for WA Status
          },
          (evt) => {
            const p = typeof evt === "number" ? evt : (evt?.progress || 0);
            const pct = Math.round(p * 100);
            const phase = evt?.phase || "encoding";
            const label = phase === "outro" ? "Adding Plyx outro"
              : phase === "loading" ? "Loading video"
              : "Preparing 30s Status clip";
            toast.loading(`${label}… ${pct}%`, { id: brandingToastId, duration: 120 * 1000 });
          },
        );
        toast.dismiss(brandingToastId);
        try { playPlyxChime(`wacut:${video.video_id}`); } catch { /* ignore */ }
        if (result?.watermarked) {
          toast.success("Saved 30s clip · plyx @" + (video.creator?.handle || "creator") + " watermark · long-press → Share → WhatsApp Status", { duration: 6000 });
        } else {
          toast.success("Saved · long-press in Gallery to post to WhatsApp Status");
        }
        return;
      } catch (brandErr) {
        toast.dismiss(brandingToastId);
        console.warn("Client-side WA branding failed:", brandErr);
        // Ultimate fallback — raw download so user always gets a file.
        const filename = `plyx-${video.creator?.handle || "video"}-status.mp4`;
        const rawUrl = plyxMediaUrl(video.video_url);
        const downloadUrl = rawUrl + (rawUrl.includes("?") ? "&" : "?") + `download=1&filename=${encodeURIComponent(filename)}`;
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = filename;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        try { playPlyxChime(`wacut:${video.video_id}`); } catch { /* ignore */ }
        toast.success("Saved · long-press in Gallery to post to WhatsApp Status");
      }
    } catch (e) {
      console.warn("Plyx WA save failed:", e);
      toast.error("Could not save this clip. Try again in a moment.");
    } finally {
      setWaSaving(false);
    }
  };

  const listing = video.listing;
  const isMine = me && video.creator?.plyx_user_id === me.plyx_user_id;

  return (
    <div className="relative w-full h-full bg-black overflow-hidden snap-start" data-testid={`plyx-video-card-${video.video_id}`}>
      {/* Media — video player or static photo with Ken-Burns zoom.
          On mobile we want a TikTok-style full-bleed (object-cover) so
          portrait clips actually fill the screen instead of sitting in
          a letterboxed window. On wider screens (≥sm) we drop back to
          object-contain so 16:9 desktop uploads aren't aggressively
          cropped. */}
      {video.media_type === "embed" && video.embed_provider === "youtube" ? (
        // External YouTube short — render in a centered 16:9 box so the
        // video fits naturally on the portrait feed without being
        // squashed or oversized.
        //
        // ─── Mute-first autoplay ──────────────────────────────────────
        // iOS Safari / Android Chrome block autoplay-with-sound until
        // the user has interacted with the page. Without muted autoplay
        // the iframe sits paused on its poster while audio from the
        // *previous* card's iframe (which the browser hasn't torn down
        // yet) keeps playing — that's the "talking but blank" feeling.
        // Muting guarantees visual playback starts; we surface a one-tap
        // "Tap for sound" pill so users can un-mute by talking to the
        // YouTube IFrame API.
        //
        // ─── Inactive cards get no autoplay & a fresh key ────────────
        // Re-keying when isActive flips forces the prior iframe to fully
        // unmount (kills its audio) before a new one mounts.
        <div
          className="absolute inset-0 flex items-center justify-center"
          onClick={handleVideoTap}
          data-testid={`plyx-embed-wrap-${video.video_id}`}
        >
          {/* Blurred YouTube thumbnail backdrop — fills the portrait
              phone frame with a heavily blurred copy of the thumbnail
              so the screen no longer shows hard black letterbox bars
              above/below 16:9 YouTube clips. The actual iframe stays
              at native 16:9 in the centre so YouTube's player isn't
              squashed or cropped. */}
          <img
            src={
              plyxMediaUrl(video.thumbnail_url) ||
              `https://i.ytimg.com/vi/${video.embed_video_id}/maxresdefault.jpg`
            }
            alt=""
            aria-hidden="true"
            onError={(e) => {
              // maxres can 404 on shorter clips; fall back to hqdefault
              // which is guaranteed to exist for every YouTube video.
              if (video.embed_video_id && !e.currentTarget.dataset.fb) {
                e.currentTarget.dataset.fb = "1";
                e.currentTarget.src = `https://i.ytimg.com/vi/${video.embed_video_id}/hqdefault.jpg`;
              }
            }}
            className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60 pointer-events-none"
          />
          {/* YouTube iframe — TikTok-style "cover" fit so the video
              fills the entire mobile screen without horizontal /
              vertical scroll. The wrapper has `overflow-hidden`; the
              child iframe is sized via CSS to be wider OR taller than
              the viewport (whichever side it needs to grow on to keep
              16:9 ratio fully covering the container) and centered
              with `translate(-50%, -50%)`. Edges get cropped — that's
              the user-requested trade-off: full-screen, fit-to-phone,
              no manual scrolling. The blurred backdrop painted
              behind this layer covers any residual gap on rare
              edge-case aspect ratios. */}
          <div
            className="relative w-full h-full overflow-hidden plyx-yt-cover-wrap"
            data-testid={`plyx-embed-fit-${video.video_id}`}
          >
            {/* Facade: only the active card (+ the NEXT card on fast
                networks) keeps a booted YouTube iframe. iter-125 removed
                the prev-card player and gated the next-card player on
                network speed — three simultaneous iframe boots were
                starving the ACTIVE video of bandwidth on phones, which
                is exactly the "most videos hang" daily complaint. Cards
                without a live player show the blurred thumbnail backdrop
                (instant) and boot on activation. */}
            {(isActive || (isNext && !PLYX_SLOW_NET)) && (
            <YouTubeEmbed
              videoId={video.embed_video_id}
              isActive={isActive}
              startMuted={muted}
              bootDelay={isActive ? 0 : 900}
              onReady={(player) => { ytPlayerRef.current = player; }}
              onPlayingChange={(playing) => { if (playing) _plyxBootFailStreak = 0; }}
              // iter-125: hard boot failure (14s active + 2 watchdog
              // reboots, still zero frames) → auto-skip. Report it so
              // clips that fail for many viewers get pulled server-side,
              // then drop the card from this viewer's feed — the next
              // clip snaps in and nobody stares at a dead spinner.
              onBootFailed={() => {
                plyxApi.post(`/plyx/videos/${video.video_id}/report-slow`)
                  .catch(() => { /* silent */ });
                _plyxBootFailStreak += 1;
                if (_plyxBootFailStreak <= 3 && typeof onEmbedRemove === "function") {
                  onEmbedRemove(video.video_id);
                }
              }}
              // When the uploader has disabled embedding (very common
              // for Vevo / major-label music videos) the iframe just
              // sits black on plyx — but the same clip plays on
              // youtube.com because there's no embed restriction
              // there. Detecting the error lets us render a clear
              // "Watch on YouTube" panel instead of users thinking
              // Plyx is broken.
              onEmbedError={() => {
                setEmbedBlocked(true);
                // Self-healing: tell the backend this clip can't be
                // embedded. The threshold is now 1 (first reporter wins)
                // so the video is removed server-side immediately. We
                // ALSO drop it from the local feed via onEmbedRemove
                // after a short delay so the viewer never sees the
                // "Watch on YouTube" fallback for more than a moment.
                plyxApi.post(`/plyx/videos/${video.video_id}/report-blocked`)
                  .catch(() => { /* silent */ });
                if (typeof onEmbedRemove === "function") {
                  setTimeout(() => onEmbedRemove(video.video_id), 1500);
                }
              }}
            />
            )}
          </div>
          {/* iter-123: TRANSPARENT GESTURE LAYER above the YouTube
              iframe — THE core "feed freezes when I swipe" fix.
              Cross-origin iframes swallow touch/wheel events, so once
              a player booted, vertical swipes landing on it were
              consumed by YouTube instead of scrolling the feed. This
              layer keeps every swipe scrolling the snap container and
              lets taps bubble to the wrapper's tap handler
              (play/pause + double-tap like now work on embeds too).
              Positioned after the player in DOM = paints above the
              iframe; overlays with explicit z (pills z-[7], blocked
              panel z-[8], iOS link z-[6]) stay on top of it. */}
          <div
            className="absolute inset-0"
            data-testid={`plyx-embed-gesture-${video.video_id}`}
          />
          {/* Embed-disabled fallback — covers black-screen YouTube
              clips with a clear, big tap target that opens the video
              on YouTube directly. */}
          {embedBlocked && video.external_url && (
            <a
              href={video.external_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`plyx-embed-blocked-${video.video_id}`}
              className="absolute inset-0 z-[8] flex flex-col items-center justify-center gap-3 bg-black/85 backdrop-blur-sm text-white text-center p-6"
            >
              <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                <span className="text-2xl">▶</span>
              </div>
              <div className="text-base font-bold">YouTube blocked this clip from Plyx</div>
              <p className="text-xs text-white/70 max-w-[280px]">
                The uploader (often Vevo or a music label) restricted embedding. Tap below to watch it on YouTube — your watch-credit on Plyx still counts.
              </p>
              <span className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-black text-sm font-bold">
                Watch on YouTube →
              </span>
            </a>
          )}
          {/* "Tap for sound" — one-tap unmute pill, shown only when the
              global mute pref is on. Tapping it flips the pref (so all
              subsequent cards in the swipe stream also play with sound)
              AND issues an unMute on the YouTube player directly. */}
          {isActive && muted && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPlyxFeedMuted(false);
                try {
                  ytPlayerRef.current?.unMute();
                  ytPlayerRef.current?.setVolume(100);
                } catch { /* ignore */ }
              }}
              data-testid={`plyx-embed-unmute-${video.video_id}`}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[7] px-5 py-2.5 rounded-full bg-black/70 backdrop-blur border border-white/25 text-sm font-semibold text-white inline-flex items-center gap-2 shadow-lg"
            >
              🔊 Tap for sound
            </button>
          )}
          {/* Always-visible "Open on YouTube" fallback for embed-blocked clips */}
          {video.external_url && isActive && (
            <a
              href={video.external_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`plyx-embed-open-yt-${video.video_id}`}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[7] px-4 py-2 rounded-full bg-black/75 backdrop-blur border border-white/20 text-xs font-semibold text-white inline-flex items-center gap-1.5"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              Watch on YouTube
            </a>
          )}
        </div>
      ) : video.media_type === "photo" ? (
        <>
          {/* Blurred backdrop fill — same image, scaled to cover the
              full viewport with heavy blur so the screen reads "full"
              even when the photo's aspect ratio doesn't match the
              phone frame. Instagram-style — eliminates the harsh
              black letterbox bars without cropping the original. */}
          {(isActive || isNext) && (
            <img
              src={plyxMediaUrl(video.video_url)}
              alt=""
              aria-hidden="true"
              loading={isActive ? "eager" : "lazy"}
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60 pointer-events-none"
            />
          )}
          <img
            src={plyxMediaUrl(video.video_url)}
            alt={video.caption || "Plyx post"}
            // User requested full-screen "auto-fit" on mobile (no
            // letterbox / no manual scroll). object-cover crops the
            // edges of landscape uploads — the trade-off they
            // explicitly asked for; the blurred backdrop above
            // still shows when the photo is portrait.
            className="relative w-full h-full object-cover cursor-pointer select-none"
            onClick={handleVideoTap}
            data-testid={`plyx-photo-${video.video_id}`}
            style={{
              filter: BEAUTIFY_CSS[video.beautify_filter] || "none",
              animation: isActive ? "plyxPhotoKenBurns 6s ease-in-out both" : "none",
            }}
          />
        </>
      ) : (
        <>
          {/* Blurred video backdrop — paint a heavily blurred copy of
              the thumbnail behind the player so portrait phones never
              show the harsh black letterbox bars on landscape uploads.
              We use the poster (a single still) rather than a second
              <video> to avoid doubling decode/network cost. */}
          {(isActive || isNext) && video.thumbnail_url && (
            <img
              src={plyxMediaUrl(video.thumbnail_url)}
              alt=""
              aria-hidden="true"
              // Perf: the blur-2xl full-screen backdrop is GPU-expensive
              // on iOS Safari — only the active + next cards paint it.
              loading={isActive ? "eager" : "lazy"}
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60 pointer-events-none"
            />
          )}
          <video
            ref={(el) => {
              // Callback ref: we control the muted state imperatively
              // because (a) JSX `muted={…}` re-applies on every render
              // and fights our unmute-on-user-opt-in path, and
              // (b) HTML/React don't expose a `defaultMuted` JSX
              // shorthand. Setting `.muted = true` right when the DOM
              // node first attaches is the earliest possible moment
              // and lets the browser-native `autoplay` attribute fire
              // immediately (which is what saves us the round-trip
              // through the React effect → race that was leaving
              // mobile users staring at a paused poster + centre
              // play button).
              if (el && videoRef.current !== el) {
                // iter-119i: only seed muted=true if the user hasn't
                // already explicitly unmuted on a previous card. This
                // lets the cross-swipe unmute preference survive the
                // first paint without the React effect race.
                try {
                  el.muted = !hasBeenUnmutedRef.current;
                  el.playsInline = true;
                } catch { /* ignore */ }
              }
              videoRef.current = el;
            }}
            src={plyxMediaUrl(video.video_url)}
            poster={plyxMediaUrl(video.thumbnail_url)}
            // Perf: the ACTIVE card buffers ahead ("auto") and the NEXT
            // card in the swipe stack ALSO preloads ("auto") so swiping
            // starts playback in <0.5s — TikTok-style instant start.
            // Other mounted cards fetch just metadata + first frame
            // (iter-121, was "none") so a fast two-slide swipe lands on
            // a card that can paint instantly instead of a black tile.
            preload={isActive || isNext ? "auto" : "none"}
            // User requested full-screen "auto-fit" on mobile.
            // object-cover fills the entire portrait viewport,
            // cropping the sides of landscape uploads — that's the
            // trade-off the user explicitly asked for.
            className="relative w-full h-full object-cover cursor-pointer select-none"
            loop
            playsInline
            // ── Native autoplay attribute (iter-111 / iter-121) ─────
            // Only the ACTIVE card carries autoplay now. Previously
            // EVERY mounted card had it — three off-screen videos all
            // fetching + starting playback at once, competing with the
            // active card for bandwidth and mobile decoder slots (a
            // big contributor to the swipe freeze on phones).
            autoPlay={isActive}
          // We don't set `muted` via React JSX — the autoplay effect
          // sets `videoRef.current.muted = true` imperatively for the
          // initial play() call (so the browser permits autoplay) and
          // then unmutes via `videoRef.current.muted = false` once
          // play() resolves AND the user has opted in. A React-bound
          // muted prop would re-mute the element on every re-render
          // and fight the Android autoplay policy.
          onClick={handleVideoTap}
          data-testid={`plyx-video-${video.video_id}`}
          style={{ filter: BEAUTIFY_CSS[video.beautify_filter] || "none" }}
        />
        </>
      )}

      {/* Removed (iter-81): the top-left "▶ Plyx + @handle" watermark
          was duplicating the brand mark we already render at the
          top-right of the feed (iter-75) AND leaking the creator's
          handle in the corner where new visitors mistook it for their
          own username on the register page. Bottom-left CreatorCard
          already shows @handle in its proper place. */}

      {/* iter-119aa: iOS transcode indicator. Only show while transcode
          is genuinely fresh (< 5 min old) so a stuck / failed transcode
          from production doesn't display this pill forever. Users were
          confused thinking this pill was related to downloads. */}
      {video.media_type !== "photo"
        && video.ios_optimised === false
        && (video.transcode_status === "queued" || video.transcode_status === "running")
        && (() => {
            const createdAt = video.created_at ? new Date(video.created_at).getTime() : 0;
            return Date.now() - createdAt < 5 * 60 * 1000;
          })() && (
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/10 text-[11px] text-white/90 inline-flex items-center gap-2 animate-pulse"
          data-testid={`plyx-video-optimising-${video.video_id}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#FFB42D]"/> Preparing for iPhone…
        </div>
      )}

      {/* External ingested content badge — RSS/news/YouTube cards show
          their origin + a tappable "Read full story" pill so users
          understand where the content came from and can open it. */}
      {video.external_url && (
        <a
          href={video.external_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="absolute top-[calc(env(safe-area-inset-top,0px)+6.5rem)] sm:top-3 right-3 z-[6] px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/15 text-[11px] font-semibold text-white inline-flex items-center gap-1.5 hover:bg-black/85 transition"
          data-testid={`plyx-external-link-${video.video_id}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-white/10" />
          {video.source === "youtube" ? "Watch on YouTube"
            : video.source === "rss" || video.source === "news" ? "Read full story"
            : "Open source"}
        </a>
      )}

      {/* Creator emoji sticker overlay — positioned to match the upload editor */}
      {Array.isArray(video.stickers) && video.stickers.length > 0 && (
        <div className="absolute inset-0 pointer-events-none" data-testid={`plyx-video-stickers-${video.video_id}`}>
          {video.stickers.map((s, i) => (
            <span
              key={i}
              className="absolute select-none"
              style={{
                left: `${(s.x ?? 0.5) * 100}%`,
                top: `${(s.y ?? 0.5) * 100}%`,
                fontSize: s.size || 48,
                transform: `translate(-50%, -50%) rotate(${s.rot || 0}deg)`,
                textShadow: "0 0 8px rgba(0,0,0,0.4)",
              }}
            >
              {s.emoji}
            </span>
          ))}
        </div>
      )}

      {/* Double-tap heart burst — fires once per double-tap, key forces re-mount */}
      {heartBurst > 0 && (
        <div
          key={heartBurst}
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          data-testid={`plyx-heart-burst-${video.video_id}`}
        >
          <Heart
            className="w-32 h-32 fill-[#FF2D7A] text-[#FF2D7A] drop-shadow-2xl"
            style={{
              animation: "plyxHeartBurst 700ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
            }}
          />
        </div>
      )}

      {/* iter-119h: Centred "Tap for sound" pill for NATIVE videos.
          Gated on the ACTUAL <video>.muted DOM state (domMuted) — not the
          user-preference state (muted) — so the pill appears whenever the
          browser autoplay policy forced muted playback, even if the
          viewer's saved preference is "unmuted". Without this, viewers
          landing fresh on /plyx/v/:id with an unmuted preference get a
          silent video and no visible affordance to unmute (recording
          20260630_015004 confirmed this regression). */}
      {video.media_type !== "photo"
        && video.media_type !== "embed"
        && video.has_audio !== false
        && isActive
        && domMuted && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setPlyxFeedMuted(false);
            try {
              if (videoRef.current) {
                videoRef.current.muted = false;
                if (videoRef.current.paused) videoRef.current.play().catch(() => {});
              }
            } catch { /* ignore */ }
          }}
          data-testid={`plyx-native-unmute-${video.video_id}`}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[7] px-5 py-2.5 rounded-full bg-black/70 backdrop-blur border border-white/25 text-sm font-semibold text-white inline-flex items-center gap-2 shadow-lg animate-pulse"
        >
          🔊 Tap for sound
        </button>
      )}

      {/* Play indicator overlay — ONLY for native <video> uploads while
          actually paused. Embeds (YouTube) and photo cards never own the
          `playing` state, so the overlay would otherwise sit permanently
          on top of those and block the content. */}
      {video.media_type !== "photo"
        && video.media_type !== "embed"
        && !playing && (
        <button
          type="button"
          onClick={togglePlay}
          aria-label="Play"
          data-testid={`plyx-play-overlay-${video.video_id}`}
          className="absolute inset-0 flex items-center justify-center text-white/80 hover:text-white"
        >
          <span className="w-20 h-20 rounded-full bg-black/40 backdrop-blur flex items-center justify-center">
            <Play className="w-10 h-10 fill-white ml-1"/>
          </span>
        </button>
      )}

      {/* Top-right mute toggle. Persists session-wide so the user
          doesn't have to re-tap after every swipe.
          - When the video has audio AND the viewer is currently muted,
            the button gets a subtle amber pulse + "Tap for sound" label
            so creators don't think the upload is broken just because
            the browser autoplay-muted by default.
          - When the source genuinely has no audio track (has_audio=
            false from the transcode probe), we render a clear "Silent"
            badge instead so the viewer knows it's not a Plyx bug. */}
      {video.has_audio === false ? (
        <div
          data-testid={`plyx-silent-${video.video_id}`}
          className="absolute right-3 sm:right-4 top-[calc(env(safe-area-inset-top,0px)+3.25rem)] sm:top-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md text-white/90 text-[11px] font-semibold border border-white/15"
          title="This clip has no audio track"
        >
          <VolumeX className="w-3.5 h-3.5" /> Silent
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPlyxFeedMuted(!muted)}
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid={`plyx-mute-${video.video_id}`}
          className={
            "absolute right-3 sm:right-4 top-[calc(env(safe-area-inset-top,0px)+3.25rem)] sm:top-4 z-20 flex items-center gap-1.5 px-3 py-2 rounded-full backdrop-blur-md text-white transition "
            + (muted && isActive
              ? "bg-amber-400/90 text-black shadow-lg animate-[plyxRibbonIn_1.2s_ease-out_infinite_alternate]"
              : "bg-black/60 hover:bg-black/80 border border-white/10 w-10 h-10 justify-center")
          }
        >
          {muted ? <VolumeX className="w-5 h-5"/> : <Volume2 className="w-5 h-5"/>}
          {muted && isActive && <span className="text-xs font-bold pr-1">Tap for sound</span>}
        </button>
      )}

      {/* Top Gifters ribbon — fades in once the active video has gifts */}
      {isActive && (giftStats.topGifters || []).length > 0 && (
        <div
          className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur border border-white/10"
          style={{ animation: "plyxRibbonIn 500ms cubic-bezier(0.22,1,0.36,1) both" }}
          data-testid={`plyx-top-gifters-${video.video_id}`}
        >
          <Trophy className="w-3.5 h-3.5 text-[#FFB42D]" />
          <span className="text-[10px] uppercase tracking-wider text-white/70">Top gifters</span>
          <div className="flex items-center -space-x-1.5 ml-1">
            {(giftStats.topGifters || []).slice(0, 3).map((g, i) => (
              <Link
                key={g.handle + i}
                to={`/plyx/u/${g.handle}`}
                title={`@${g.handle} · ${g.coins_total} coins`}
                data-testid={`plyx-top-gifter-${video.video_id}-${i}`}
                className="relative w-7 h-7 rounded-full bg-white/15 border border-white/30 overflow-hidden flex items-center justify-center text-[11px] font-semibold hover:scale-110 transition-transform"
                style={{ zIndex: 10 - i }}
              >
                {g.avatar_url ? (
                  <img src={g.avatar_url} alt={g.handle} className="w-full h-full object-cover" />
                ) : (
                  <span>{(g.display_name || g.handle || "?").charAt(0).toUpperCase()}</span>
                )}
                <span
                  className="absolute -bottom-0.5 -right-0.5 text-[10px]"
                  style={{ textShadow: "0 0 4px rgba(0,0,0,0.6)" }}
                >
                  {g.top_gift_emoji}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Bottom-left: creator + caption + listing
          Lifted up on mobile so the `plyx @username` row clears the
          bottom navigation + the BBC news ticker (which sits at
          bottom-16). The user's repeated "people are struggling to
          see it, it's too down" feedback was about exactly this
          overlap. Desktop keeps the original near-bottom position
          since there's no bottom-nav at sm+ widths. */}
      <div className="absolute bottom-20 sm:bottom-4 left-0 right-16 p-4 sm:p-6 pt-2 sm:pt-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-white">
        {!video.creator && (
          <div className="mb-2">
            <span
              data-testid={`plyx-brand-pill-${video.video_id}`}
              className="inline-flex items-center px-2 py-[2px] rounded-full text-[10px] font-bold uppercase tracking-wider text-[#0A0A0A]"
              style={{ background: "#E85D4E" }}
            >
              plyx
            </span>
          </div>
        )}
        {video.creator && (
          <div className="flex items-center gap-3 mb-2">
            <Link to={`/plyx/u/${video.creator.handle}`} className="flex items-center gap-2 hover:opacity-80" data-testid={`plyx-creator-link-${video.video_id}`}>
              <div className="w-9 h-9 rounded-full bg-white/15 overflow-hidden flex items-center justify-center text-white font-semibold">
                {video.creator.avatar_url
                  ? <img src={plyxMediaUrl(video.creator.avatar_url)} alt={video.creator.handle} className="w-full h-full object-cover"/>
                  : (video.creator.display_name?.slice(0, 1) || "P")}
              </div>
              <div>
                <div className="font-bold text-base inline-flex items-center gap-1.5" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
                  <span
                    data-testid={`plyx-badge-${video.video_id}`}
                    className="px-1.5 py-[1px] rounded-full text-[9px] font-bold uppercase tracking-wider text-[#0A0A0A]"
                    style={{ background: "#E85D4E" }}
                  >
                    plyx
                  </span>
                  @{video.creator.handle}
                  <VerifiedBadge user={video.creator} size={14} />
                </div>
                {video.creator.country && <div className="text-[13px] font-medium text-white/80" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.8)" }}>{video.creator.country}</div>}
              </div>
            </Link>
            {!isMine && (
              <button
                type="button"
                onClick={handleFollow}
                data-testid={`plyx-follow-${video.video_id}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  followed ? "bg-white/15 text-white hover:bg-white/25" : "bg-white text-[#0A0A0A] hover:bg-white/90"
                }`}
              >
                {followed ? "Following" : "Follow"}
              </button>
            )}
          </div>
        )}
        {video.caption && (
          <p className="text-sm leading-relaxed max-w-md whitespace-pre-wrap line-clamp-3" data-testid={`plyx-caption-${video.video_id}`}>
            {video.caption}
          </p>
        )}
        {video.tags?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {video.tags.slice(0, 5).map((t) => {
              const slug = String(t).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
              if (!slug) return null;
              return (
                <Link
                  key={t}
                  to={`/plyx/tag/${slug}`}
                  data-testid={`plyx-caption-tag-${slug}`}
                  className="text-xs text-[#FFB42D] hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  #{t}
                </Link>
              );
            })}
          </div>
        )}
        {listing && (
          <Link
            to={`/listing/${listing.listing_id}?plyx_v=${video.video_id}`}
            data-testid={`plyx-buy-${video.video_id}`}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#0A0A0A] text-sm font-semibold hover:bg-white/90 transition-colors"
          >
            <ShoppingBag className="w-4 h-4"/>
            Shop · {listing.images?.[0] ? <img src={listing.images[0]} alt="" className="w-5 h-5 rounded object-cover"/> : null}
            <span className="truncate max-w-[140px]">{listing.title}</span>
            <span className="font-bold">{currencySymbol(listing.currency)}{listing.price}</span>
          </Link>
        )}
        {video.tagged_listings?.length > 0 && (
          <button
            type="button"
            onClick={() => setShopSheetOpen(true)}
            data-testid={`plyx-shop-chip-${video.video_id}`}
            className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] hover:bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white text-xs font-semibold transition-colors"
          >
            <ShoppingBag className="w-3.5 h-3.5"/>
            Shop {video.tagged_listings.length} item{video.tagged_listings.length === 1 ? "" : "s"}
          </button>
        )}
      </div>

      {/* Tag-and-Sell bottom sheet */}
      {shopSheetOpen && video.tagged_listings?.length > 0 && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end justify-center" onClick={() => setShopSheetOpen(false)} data-testid={`plyx-shop-sheet-${video.video_id}`}>
          <div
            className="w-full max-w-md bg-[#0A0A0A] border-t border-white/10 rounded-t-3xl p-5 max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#FFB42D]"/> Shop this clip
              </h3>
              <button
                type="button"
                onClick={() => setShopSheetOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
                data-testid={`plyx-shop-sheet-close-${video.video_id}`}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3">
              {video.tagged_listings.map((p) => (
                <a
                  key={p.listing_id}
                  href={p.listing_url}
                  target="_top"
                  data-testid={`plyx-shop-sheet-item-${p.listing_id}`}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:border-[#FFB42D] transition-colors"
                >
                  <div className="w-16 h-16 rounded-lg bg-black/50 overflow-hidden shrink-0">
                    {p.thumbnail_url
                      ? <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy"/>
                      : <div className="w-full h-full flex items-center justify-center text-white/20"><ShoppingBag className="w-6 h-6"/></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate text-white">{p.title}</div>
                    <div className="text-base font-display text-[#FFB42D] mt-0.5 tabular-nums">
                      {currencySymbol(p.currency)}{p.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    {!p.is_buyable && <div className="text-[10px] text-red-400 uppercase tracking-wider">Unavailable</div>}
                  </div>
                  <div className="px-3 py-1.5 rounded-full bg-white text-[#0A0A0A] text-xs font-bold shrink-0">
                    Buy →
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Right action rail — icons carry a drop-shadow via the container
          filter so they stay legible on bright video backgrounds. */}
      <div
        className="absolute right-2.5 sm:right-4 bottom-44 sm:bottom-32 flex flex-col items-center gap-6 text-white"
        style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.75))" }}
      >
        <button type="button" onClick={handleLike} data-testid={`plyx-like-${video.video_id}`} className="flex flex-col items-center hover:scale-110 transition-transform">
          <Heart className={`w-9 h-9 ${liked ? "fill-[#FF2D7A] text-[#FF2D7A]" : ""}`}/>
          <span className="text-xs font-semibold mt-1">{fmtCount(likeCount)}</span>
        </button>
        <button type="button" onClick={() => onCommentClick?.(video)} data-testid={`plyx-comment-${video.video_id}`} className="flex flex-col items-center hover:scale-110 transition-transform">
          <MessageCircle className="w-9 h-9"/>
          <span className="text-xs font-semibold mt-1">{fmtCount(video.comment_count)}</span>
        </button>
        <button type="button" onClick={handleShare} data-testid={`plyx-share-${video.video_id}`} className="flex flex-col items-center hover:scale-110 transition-transform">
          <Share2 className="w-9 h-9"/>
          <span className="text-xs font-semibold mt-1">{fmtCount(video.share_count)}</span>
        </button>
        {/* Repost / Save / Duet moved into the Share sheet (declutter). */}
        <button
          type="button"
          onClick={() => {
            if (!me) {
              toast.error("Sign in to send gifts");
              navigate("/plyx/login");
              return;
            }
            if (me?.plyx_user_id === video.creator_id) {
              toast.info("You can't gift your own video — share it instead!");
              return;
            }
            setGiftDrawerOpen(true);
          }}
          data-testid={`plyx-gift-btn-${video.video_id}`}
          className="flex flex-col items-center hover:scale-110 transition-transform"
        >
          <Gift className="w-9 h-9 text-[#FFB42D]" style={{ filter: "drop-shadow(0 0 6px rgba(255,180,45,0.4))" }} />
          <span className="text-xs font-semibold mt-1">{fmtCount(giftStats.total || 0)}</span>
        </button>

        {/* Attribution chip — shown on duet videos so viewers can jump to the original */}
        {video.duet_of_video_id && video.duet_original_creator && (
          <Link
            to={`/plyx/u/${video.duet_original_creator.handle}`}
            data-testid={`plyx-duet-attribution-${video.video_id}`}
            className="flex flex-col items-center hover:scale-105 transition-transform"
            title={`Duet with @${video.duet_original_creator.handle}`}
          >
            <div className="relative">
              <Layers className="w-7 h-7 text-white/85" />
              <span className="absolute -bottom-1 -right-1 text-[10px]">↩</span>
            </div>
            <span className="text-[10px] font-semibold mt-1 truncate max-w-[64px]">
              @{video.duet_original_creator.handle}
            </span>
          </Link>
        )}
      </div>

      {/* Floating-emoji gift overlay — animates up & out */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid={`plyx-gift-stream-${video.video_id}`}>
        {floatingGifts.map((g) => (
          <span
            key={g.id}
            className="absolute"
            style={{
              left: `${g.lane}%`,
              bottom: 80,
              fontSize: g.size,
              animation: "plyxGiftRise 3s cubic-bezier(0.22,1,0.36,1) forwards",
              filter: "drop-shadow(0 0 10px rgba(255,180,45,0.6))",
            }}
          >
            {g.emoji}
          </span>
        ))}
      </div>

      {/* Auto-surface translate chip — appears when video language ≠
          viewer's browser locale and captions are ready. One-tap
          translates this clip + remembers the preference for future
          swipes. Dismiss = "X". */}
      {/* (Removed iter-107) Auto-popping "Watch in {Lang}?" chip — was
          covering the lower-left and felt intrusive when users were
          just trying to scroll through the feed. The permanently
          visible 🌍 Translate pill below now serves the same purpose
          without surprising the viewer. */}


      {/* Whisper auto-caption line */}
      {captionsOn && captionLine && (
        <div
          className="absolute left-3 right-20 z-20 pointer-events-none"
          style={{ bottom: 110 }}
          data-testid={`plyx-caption-${video.video_id}`}
        >
          <div className="inline-block max-w-full px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur text-white text-[14px] leading-snug font-medium"
               style={{ textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
            {captionLine}
          </div>
        </div>
      )}

      {/* Caption / Translate pill — ALWAYS VISIBLE so every video has
          a discoverable translate entry-point.  Three states:

            • ready / ready_yt → CC toggle + Translate button (ALWAYS
              visible so the viewer can click and pick a language; we
              no longer require captions-on first per user feedback).
            • generating       → small "Captions…" spinner pill
            • missing          → "🌍 Translate" button that on tap
                                 generates captions on-demand (native)
                                 or yanks YT captions via yt-dlp.

          Vertical placement: must clear the BBC news ticker which sits
          fixed at bottom-16 (mobile) / sm:bottom-20 (desktop) and is
          ~36px tall — so the ticker band occupies roughly 64-104px on
          mobile and 80-116px on desktop. Anchoring this pill at
          bottom-36 (mobile) / sm:bottom-32 (desktop) parks it cleanly
          above the ticker so the viewer can always tap "Pick language".
      */}
      <div className="absolute bottom-36 sm:bottom-32 left-4 z-30 flex items-center gap-1" data-testid={`plyx-cc-zone-${video.video_id}`}>
        {(video.captions_status === "ready" || video.captions_status === "ready_yt" || captionSegs?.length > 0) ? (
          <>
            <button
              type="button"
              onClick={() => setCaptionsOn((c) => !c)}
              data-testid={`plyx-cc-toggle-${video.video_id}`}
              title={captionsOn ? "Turn captions off" : "Turn captions on"}
              className={`px-2 py-1 rounded text-[10px] font-bold tracking-wider border transition-colors
                          ${captionsOn ? "bg-white text-[#0A0A0A] border-white" : "bg-black/60 backdrop-blur-md text-white/85 border-white/30"}`}
            >
              CC
            </button>
            <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    // Tapping the language picker turns captions ON
                    // automatically — otherwise the picker would
                    // surface but the chosen language wouldn't show.
                    if (!captionsOn) setCaptionsOn(true);
                    setLangMenuOpen((o) => !o);
                  }}
                  data-testid={`plyx-cc-lang-btn-${video.video_id}`}
                  title="Translate captions"
                  className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white inline-flex items-center gap-1 shadow-md hover:bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] transition-colors"
                >
                  <Languages className="w-3 h-3"/>
                  {captionLang === "auto" ? "Pick language ▾" : `${captionLang.toUpperCase()} ▾`}
                </button>
                {langMenuOpen && (
                  <div
                    className="absolute bottom-full mb-1 left-0 z-30 min-w-[160px] max-h-72 overflow-y-auto rounded-md bg-[#0A0A0A] border border-white/15 shadow-xl py-1"
                    data-testid={`plyx-cc-lang-menu-${video.video_id}`}
                  >
                    {/* Header strip — clarifies that translations are
                        a Plyx Pro perk so users don't tap blindly. */}
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-white/50 border-b border-white/10 flex items-center justify-between">
                      <span>Translate to…</span>
                      {!me?.is_plyx_pro && (
                        <span
                          className="text-[9px] font-bold px-1.5 py-[1px] rounded-sm bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white"
                          data-testid={`plyx-cc-lang-pro-badge-${video.video_id}`}
                        >
                          PRO
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setCaptionLang("auto");
                        setTranslatedSegs(null);
                        setLangMenuOpen(false);
                      }}
                      data-testid={`plyx-cc-lang-opt-auto-${video.video_id}`}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-white/10
                                  ${captionLang === "auto" ? "text-[#FFB42D]" : "text-white/90"}`}
                    >
                      Original (free)
                    </button>
                    {[
                      {code:"en",label:"English"},{code:"fr",label:"French"},{code:"es",label:"Spanish"},
                      {code:"pt",label:"Portuguese"},{code:"ar",label:"Arabic"},{code:"sw",label:"Swahili"},
                      {code:"yo",label:"Yoruba"},{code:"ig",label:"Igbo"},{code:"ha",label:"Hausa"},
                      {code:"zu",label:"Zulu"},{code:"am",label:"Amharic"},{code:"zh",label:"Chinese"},
                      {code:"hi",label:"Hindi"},
                    ].map((L) => (
                      <button
                        key={L.code}
                        type="button"
                        onClick={async () => {
                          setLangMenuOpen(false);
                          if (captionLang === L.code && translatedSegs) return;
                          // Cached translations (same lang on same
                          // video) are free for everyone, so check
                          // BEFORE applying the Pro gate.
                          const cached = (video.translations || {})[L.code]?.segments?.length > 0;
                          // Pro gate (iter-113): per user direction
                          // only Plyx Pro subscribers can kick off a
                          // brand-new translation. Free-tier viewers
                          // hit the paywall toast immediately — no
                          // round-trip to the backend, no wasted UX
                          // beats. We still go through the server for
                          // cached translations since they're free.
                          if (!cached && !me?.is_plyx_pro) {
                            toast.error("Translation is a Plyx Pro perk", {
                              description: `Upgrade to translate any clip into ${L.label}. Original language is always free.`,
                              action: {
                                label: "Go Pro",
                                onClick: () => { try { window.location.href = "/plyx/pro"; } catch { /* noop */ } },
                              },
                              duration: 5500,
                            });
                            return;
                          }
                          if (!cached) {
                            const ok = window.confirm(
                              `Translate this clip's captions to ${L.label}?\n\n`
                              + `Uses one of your Plyx Pro LLM credits (free if you pick the same language on this video again — cached for all viewers).\n\n`
                              + `Tap Cancel to keep captions in the original language.`
                            );
                            if (!ok) return;
                          }
                          try {
                            toast.message(`Translating to ${L.label}…`, { duration: 1200 });
                            const r = await plyxApi.post(
                              `/plyx/videos/${video.video_id}/captions/translate`,
                              { lang: L.code },
                            );
                            if (r.data?.segments?.length) {
                              setTranslatedSegs(r.data.segments);
                              setCaptionLang(L.code);
                              toast.success(`Captions in ${L.label}${r.data.cached ? " (cached, free)" : " ✨"}`);
                            } else {
                              toast.error("No translation returned");
                            }
                          } catch (e) {
                            // 402 → Plyx Pro required. Show a paywall
                            // toast that links to the Pro upgrade flow
                            // instead of a generic error.
                            const status = e.response?.status;
                            const detail = e.response?.data?.detail;
                            const code = (detail && typeof detail === "object") ? detail.code : null;
                            if (status === 402 || code === "pro_required") {
                              toast.error("Translation is a Plyx Pro perk", {
                                description: (typeof detail === "object" && detail.message)
                                  || "Upgrade to translate captions on any clip — Original language is always free.",
                                action: {
                                  label: "Go Pro",
                                  onClick: () => { try { window.location.href = "/plyx/pro"; } catch { /* noop */ } },
                                },
                                duration: 5500,
                              });
                            } else {
                              toast.error(
                                (typeof detail === "string" && detail)
                                || (detail && detail.message)
                                || "Translation failed"
                              );
                            }
                          }
                        }}
                        data-testid={`plyx-cc-lang-opt-${L.code}-${video.video_id}`}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-white/10
                                    ${captionLang === L.code ? "text-[#FFB42D]" : "text-white/90"}`}
                      >
                        {L.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
          </>
        ) : (
          <CaptionRequestButton
            video={video}
            onSegmentsReady={(segs, lang) => {
              setCaptionSegs(segs);
              if (lang) setCaptionLang(lang);
              // Captions arrive but stay HIDDEN (iter-114). The viewer
              // must deliberately tap CC to show them. Auto-showing
              // looked like automatic translation when the source
              // language wasn't English. The dropdown also stays
              // closed (iter-112) so accidental taps don't spend
              // Pro credits.
            }}
          />
        )}
      </div>

      {/* Gift drawer */}
      <GiftDrawer
        open={giftDrawerOpen}
        onClose={() => setGiftDrawerOpen(false)}
        videoId={video.video_id}
        onGiftSent={handleGiftSent}
      />

      <style>{`
        @keyframes plyxGiftRise {
          0%   { transform: translate(0, 0) rotate(-8deg) scale(0.6); opacity: 0; }
          15%  { transform: translate(0, -30px) rotate(-8deg) scale(1.15); opacity: 1; }
          70%  { transform: translate(0, -260px) rotate(8deg) scale(1); opacity: 1; }
          100% { transform: translate(0, -360px) rotate(0deg) scale(0.85); opacity: 0; }
        }
        @keyframes plyxRibbonIn {
          0%   { transform: translateY(-10px); opacity: 0; }
          100% { transform: translateY(0);     opacity: 1; }
        }
        @keyframes plyxPhotoKenBurns {
          0%   { transform: scale(1.0)  translate(0, 0); }
          100% { transform: scale(1.08) translate(-1.5%, 1.5%); }
        }
      `}</style>

      {/* Bottom scrubber — thin progress line, TikTok-style. Only visible while active. */}
      {shareOpen && (
        <div className="fixed inset-0 z-[80]" data-testid={`plyx-share-sheet-${video.video_id}`}>
          <button
            type="button"
            aria-label="Close share sheet"
            data-testid="plyx-share-sheet-backdrop"
            className="absolute inset-0 bg-black/60"
            onClick={() => setShareOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 rounded-t-3xl bg-[#141414] border-t border-white/10 p-5 pb-[calc(env(safe-area-inset-bottom,0px)+2rem)] max-w-lg mx-auto">
            <div className="text-sm font-bold text-white mb-4">Share this clip</div>
            <div className="grid gap-2">
              <button
                type="button"
                onClick={doNativeShare}
                data-testid="plyx-share-native"
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                <Share2 className="w-5 h-5 text-[#FF2D7A]" /> Share…
              </button>
              <button
                type="button"
                onClick={doCopyLink}
                data-testid="plyx-share-copy-link"
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                <Link2 className="w-5 h-5 text-[#FFB42D]" /> Copy link
              </button>
              <button
                type="button"
                onClick={doCopyEmbed}
                data-testid="plyx-share-copy-embed"
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                <Code2 className="w-5 h-5 text-[#00E0C7]" />
                <span className="text-left">
                  Copy embed code
                  <span className="block text-[11px] font-normal text-white/50">For websites &amp; blogs — links back to Plyx</span>
                </span>
              </button>
              {me?.plyx_user_id !== video.creator_id && (
                <button
                  type="button"
                  onClick={() => { setShareOpen(false); handleRepost(); }}
                  disabled={repostBusy}
                  data-testid={`plyx-repost-${video.video_id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-60"
                >
                  <Repeat2 className={`w-5 h-5 ${reposted ? "text-[#00E0C7]" : "text-white/80"}`} />
                  <span className="text-left">
                    {reposted ? "Remove repost" : "Repost to your profile"}
                    {repostCount > 0 && <span className="block text-[11px] font-normal text-white/50">{fmtCount(repostCount)} reposts</span>}
                  </span>
                </button>
              )}
              <button
                type="button"
                onClick={() => { setShareOpen(false); handleDownload(); }}
                disabled={downloading}
                data-testid={`plyx-download-${video.video_id}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-60"
              >
                {downloading ? <Loader2 className="w-5 h-5 animate-spin text-[#FFB42D]" /> : <Download className="w-5 h-5 text-white/80" />}
                <span className="text-left">
                  {downloading ? `Saving… ${Math.round(downloadProgress * 100)}%` : "Save video"}
                  <span className="block text-[11px] font-normal text-white/50">Downloads with the Plyx watermark</span>
                </span>
              </button>
              {video.media_type !== "photo" && video.duets_enabled !== false && me?.plyx_user_id !== video.creator_id && (
                <button
                  type="button"
                  onClick={() => {
                    setShareOpen(false);
                    if (!me) {
                      navigate("/plyx/login", { state: { from: `/plyx/duet/${video.video_id}` } });
                      return;
                    }
                    navigate(`/plyx/duet/${video.video_id}`);
                  }}
                  data-testid={`plyx-duet-btn-${video.video_id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
                >
                  <Layers className="w-5 h-5 text-white/80" /> Duet this video
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {isActive && (
        <div
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/20 pointer-events-none"
          data-testid={`plyx-scrubber-${video.video_id}`}
        >
          <div
            className="h-full bg-white/90 transition-[width] duration-150 ease-linear"
            style={{ width: `${(progress * 100).toFixed(2)}%` }}
          />
        </div>
      )}
    </div>
  );
};

// Memoized: stable props from the virtualized feed mean untouched cards
// skip re-rendering entirely on every activeIdx change — key for 60fps
// scrolling on iOS Safari.
export default React.memo(VideoCard);
